import ArchiveIcon from "@mui/icons-material/Archive";
import EditIcon from "@mui/icons-material/Edit";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTranslation } from "react-i18next";

import type { Organization } from "@/api/types";
import { StatusChip } from "@/components/StatusChip";

export function OrganizationCardGrid({
  rows,
  loading,
  pinnedSet,
  onTogglePin,
  onEdit,
  onArchive,
  canUpdate,
  canDelete,
}: {
  rows: Organization[];
  loading: boolean;
  pinnedSet: Set<string>;
  onTogglePin: (id: string) => void;
  onEdit: (org: Organization) => void;
  onArchive: (id: string) => void;
  canUpdate: boolean;
  canDelete: boolean;
}) {
  const { t } = useTranslation();
  if (loading) {
    return (
      <Grid container spacing={2}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
            <Skeleton variant="rounded" height={140} />
          </Grid>
        ))}
      </Grid>
    );
  }
  if (rows.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          {t("orgs.empty")}
        </Typography>
      </Box>
    );
  }
  return (
    <Grid container spacing={2}>
      {rows.map((o) => {
        const pinned = pinnedSet.has(o.id);
        return (
          <Grid key={o.id} size={{ xs: 12, sm: 6, md: 4 }}>
            <Card variant="outlined" sx={{ position: "relative" }}>
              <IconButton
                size="small"
                onClick={() => onTogglePin(o.id)}
                color={pinned ? "warning" : "default"}
                sx={{ position: "absolute", top: 4, right: 4 }}
                aria-label={pinned ? t("orgs.unpin") : t("orgs.pin")}
              >
                {pinned ? (
                  <StarIcon fontSize="small" />
                ) : (
                  <StarBorderIcon fontSize="small" />
                )}
              </IconButton>
              <CardContent>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontFamily: "monospace" }}
                >
                  {o.code}
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap title={o.name}>
                  {o.name}
                </Typography>
                <Stack direction="row" spacing={0.75} sx={{ mt: 1 }} alignItems="center">
                  <Chip size="small" variant="outlined" label={o.kind} />
                  <StatusChip value={o.status} />
                </Stack>
                <Stack
                  direction="row"
                  spacing={0.5}
                  justifyContent="flex-end"
                  sx={{ mt: 1.5 }}
                >
                  {canUpdate && (
                    <Tooltip title={t("orgs.edit")}>
                      <IconButton size="small" onClick={() => onEdit(o)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDelete && o.status !== "ARCHIVED" && (
                    <Tooltip title={t("orgs.archive")}>
                      <IconButton
                        size="small"
                        color="warning"
                        onClick={() => {
                          if (confirm(t("orgs.archiveConfirm"))) {
                            onArchive(o.id);
                          }
                        }}
                      >
                        <ArchiveIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}
